/**
 * Whether a bare letter is the app's to act on, or belongs to whatever has focus.
 *
 * The single-letter bindings in `App.tsx` are installed on the window and read
 * the selection, so a letter pressed anywhere acts on the node the reader last
 * clicked. That is the point of them, and it was also the defect a usability
 * study found: with a node selected and a button in the thread holding focus, a
 * person typing a question into what they took for the question box fired a
 * paid Deepen on its first "d". The old guard asked only whether focus was in a
 * text field, and a focused button, link or menu is not one — yet every letter
 * typed at it is a letter somebody meant for somewhere else.
 *
 * So the rule is the other way round. A letter is the app's when focus is on
 * nothing in particular (the page body, where a click on the canvas leaves it),
 * inside one of the surfaces the letters were designed for, or on a focusable
 * box that is not itself a control — the coach's card focuses its own section
 * when it appears, precisely so the keys keep working over it. Anywhere else a
 * control has focus, and the letter is left to it: no verb, no toast, nothing
 * spent. Silence is the right answer there for the reason it is in a text
 * field — the person was not talking to the graph.
 *
 * Pure, and structural over the element rather than typed against the DOM, so
 * the whole table is exercised in Node without a document.
 */

/** The part of the focused element this reads. `Element` satisfies it. */
export interface FocusView {
  readonly tagName: string
  readonly isContentEditable?: boolean
  getAttribute(name: string): string | null
  closest(selectors: string): unknown
}

/**
 * Where a letter stays the app's even though a button there holds focus.
 *
 * - `.labels` — the graph's captions and, inside the same host, the node list
 *   screen-reader and keyboard users select through. Clicking a caption focuses
 *   it (a `tabIndex={-1}` button still takes focus from a click), and selecting a
 *   node is exactly what comes before a verb.
 * - `.cuepins` — the numbered pins on the graph, buttons over the canvas.
 * - `.coach` — the coach's card, whose own keys (J, K, S, W, O) are for walking
 *   it; a letter pressed on its Take or Skip is not somebody typing a sentence.
 * - `.toasts` — the toast stack. Focus lands on a toast's action only where a
 *   keyboard press left it on the page body (ui/Dialogs.tsx), where the letters
 *   were the app's; a letter pressed at "Undo" is no more a sentence than one
 *   pressed at Skip, and without the zone the verbs went quiet until the toast
 *   was put down.
 *
 * None of the four holds a text field, and a field is refused before this is
 * asked, so a field added to one later still keeps its letters.
 */
export const LETTER_ZONES = '.labels, .cuepins, .coach, .toasts'

/** Elements that take typed text: never the app's, wherever they sit. */
const FIELD_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

/** Controls by element. `A` is a link whether or not it wears a role; an
 *  `OPTION` or `SUMMARY` answers to keys of its own. */
const CONTROL_TAGS = new Set([
  'BUTTON',
  'A',
  'SUMMARY',
  'OPTION',
  'IFRAME',
  'EMBED',
  'OBJECT',
  'AUDIO',
  'VIDEO',
])

/**
 * Roles that take keys — every ARIA widget role, composite or not. A `div`
 * wearing one of these is a control by contract, and the letters are its: a
 * listbox and a tree use them for typeahead, a tab and a menu item are pressed.
 * Structural roles (`group`, `region`, `img`, `dialog`…) are absent on purpose:
 * focus parked on a container is not a control being operated.
 */
const KEY_ROLES = new Set([
  'button',
  'checkbox',
  'combobox',
  'grid',
  'gridcell',
  'link',
  'listbox',
  'menu',
  'menubar',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'option',
  'radio',
  'radiogroup',
  'scrollbar',
  'searchbox',
  'slider',
  'spinbutton',
  'switch',
  'tab',
  'tablist',
  'textbox',
  'tree',
  'treegrid',
  'treeitem',
])

/** True when this element takes typed text, whether by tag, by editing, or by role. */
export function takesText(el: FocusView): boolean {
  if (FIELD_TAGS.has(el.tagName.toUpperCase())) return true
  if (el.isContentEditable) return true
  const role = el.getAttribute('role')
  return role === 'textbox' || role === 'searchbox' || role === 'combobox'
}

/**
 * May a bare letter act on the graph with this element focused?
 *
 * `null` is the document having no focused element at all, which is the body's
 * case by another name.
 */
export function letterKeyMayAct(el: FocusView | null): boolean {
  if (el === null) return true
  const tag = el.tagName.toUpperCase()
  if (tag === 'BODY' || tag === 'HTML') return true
  // A field first, and wherever it is: a zone's exemption is for its buttons.
  if (takesText(el)) return false
  if (el.closest(LETTER_ZONES)) return true
  if (CONTROL_TAGS.has(tag)) return false
  const roles = (el.getAttribute('role') ?? '').trim().split(/\s+/)
  if (roles.some((role) => KEY_ROLES.has(role))) return false
  return true
}
